import React, { useEffect, useState, useRef, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { MiniBubble } from "../ChatBubble";
import type { MiniBubbleMessage } from "../ChatBubble";
import {
  getGridMessagesCache,
  setGridMessagesCache,
} from "../../../lib/setterPerformanceCacheService";
import { getBucketColor } from "./bucketColors";
import { CopyConversationButton } from "../CopyConversationButton";
import { serializeConversation } from "../../../utils/conversationToText";
import { BulkOpenInGHL } from "./BulkOpenInGHL";
import { BulkDismiss } from "./BulkDismiss";
import { CardActions } from "./CardActions";
import { useGhlLocationId } from "../../../hooks/useGhlLocationId";
import type { ConversationNav } from "../../../hooks/data-fetching/useConversationPreviews";

// =============================================================================
// PROOF GRID VIEW
// =============================================================================
// PURPOSE: Renders ALL proof conversations in a 3-column grid with inline chat
// bubbles. Each card shows the full conversation with metric-relevant messages
// highlighted using amber rings (same system as ConversationViewModal).
//
// DATA: Fetches messages for all conversations in a single Supabase query,
// groups client-side, renders each card with auto-scroll to highlighted message.
// =============================================================================

interface Message extends MiniBubbleMessage {
  id: string;
}

interface ConversationEntry {
  conversation_id: string;
  contact_name: string | null;
  lead_instagram: string | null;
  bucket: string;
  time_label: string;
  /** Sidecar badge — only set for reply_turn_distribution + Window mode.
   *  Format: "{N} lifetime". Lets users see in-window count (time_label) AND
   *  lifetime count side-by-side without conflating which drove the bucket. */
  lifetime_label?: string;
  instanceCount?: number;
  highlightTimestamps?: string[];
  anchorTimestamps?: string[];
}

interface ProofGridViewProps {
  conversations: ConversationEntry[];
  organizationId: string;
  timezone: string;
  onConversationClick: (
    conversationId: string,
    contactName: string,
    leadInstagram: string | null,
    highlightTimestamps?: { highlight: string; anchor?: string },
    nav?: ConversationNav
  ) => void;
  /** When set, messages outside this range are dimmed */
  activeDateRange?: { start: Date; end: Date } | null;
  /** GHL user id of the setter whose grid is being viewed; null = org-wide view. */
  viewedSetterId?: string | null;
  /** Caller's profiles.ghl_user_id — for own-vs-any RBAC decision in BulkDismiss. */
  currentUserGhlId?: string | null;
  /** Called after a successful bulk dismiss/kill so parent can clear caches + refetch. */
  onAfterBulkDismiss?: (ids: string[], dismissType: "dismissed" | "dead") => void;
}

// ─── Timestamp matching (supports multiple highlights) ───────────────────────

function findClosestMessageIdx(messages: Message[], timestamp: string | undefined): number {
  if (!timestamp || messages.length === 0) return -1;
  const targetMs = new Date(timestamp).getTime();
  let bestIdx = -1;
  let bestDiff = Infinity;
  for (let i = 0; i < messages.length; i++) {
    const diff = Math.abs(new Date(messages[i].sent_at).getTime() - targetMs);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }
  return bestDiff <= 60_000 ? bestIdx : -1;
}

// Find ALL message indices matching any timestamp in the array
function findAllMatchingIndices(
  messages: Message[],
  timestamps: string[] | undefined
): Set<number> {
  const indices = new Set<number>();
  if (!timestamps || timestamps.length === 0 || messages.length === 0) return indices;

  for (const ts of timestamps) {
    const idx = findClosestMessageIdx(messages, ts);
    if (idx !== -1) indices.add(idx);
  }
  return indices;
}

function getHighlightType(
  msgIdx: number,
  highlightIndices: Set<number>,
  anchorIndices: Set<number>
): "highlight" | "anchor" | undefined {
  if (highlightIndices.has(msgIdx)) return "highlight";
  if (anchorIndices.has(msgIdx)) return "anchor";
  return undefined;
}

// ─── Conversation Card ───────────────────────────────────────────────────────

const ConversationCard: React.FC<{
  conv: ConversationEntry;
  messages: Message[];
  timezone: string;
  onHeaderClick: () => void;
  activeDateRange?: { start: Date; end: Date } | null;
  /** Animation delay in ms — scroll waits for the card's fade-in to complete */
  animationDelayMs?: number;
  selectMode: boolean;
  selected: boolean;
  hasContact: boolean;
  onToggleSelect: () => void;
  contactId: string | null;
  locationId: string | null;
  viewedSetterId?: string | null;
  currentUserGhlId?: string | null;
  onAfterDismiss: (ids: string[], dismissType: "dismissed" | "dead") => void;
}> = ({
  conv,
  messages,
  timezone,
  onHeaderClick,
  activeDateRange,
  animationDelayMs = 0,
  selectMode,
  selected,
  hasContact,
  onToggleSelect,
  contactId,
  locationId,
  viewedSetterId,
  currentUserGhlId,
  onAfterDismiss,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const firstInRangeRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);

  // Find ALL matching indices for multi-event conversations
  const highlightIndices = findAllMatchingIndices(messages, conv.highlightTimestamps);
  const anchorIndices = findAllMatchingIndices(messages, conv.anchorTimestamps);

  // No explicit highlights = scroll to the last message instead
  const hasExplicitHighlight = highlightIndices.size > 0;

  // Track if we've assigned the scroll ref to the first highlight
  let firstHighlightAssigned = false;
  let firstInRangeAssigned = false;

  // Auto-scroll WITHIN the card's scroll container (not the page).
  // Uses scrollRef (the card's scrollable div) as the scroll context,
  // not scrollIntoView which scrolls the whole page and causes the
  // "scrolled to bottom" issue when 37 cards all fight for scroll position.
  useEffect(() => {
    if (!scrollRef.current || messages.length === 0) return;
    // Use the same scroll mechanism that works for all other metrics:
    // find a target element and scroll to it within the card's scroll container.
    // For metrics with highlights, scroll to the highlight.
    // For metrics without highlights (reply_turn_distribution), scroll to the
    // bottom anchor — the last message in the conversation.
    const scrollToTarget = () => {
      if (!scrollRef.current) return;
      const container = scrollRef.current;
      // Priority: explicit highlight > first in-range > last message (bottom)
      // When no explicit highlight (e.g. reply_turn_distribution), skip firstInRangeRef —
      // it targets the FIRST message today, not the last. Go straight to lastMessageRef.
      const target = hasExplicitHighlight
        ? highlightRef.current || firstInRangeRef.current
        : lastMessageRef.current;
      if (target) {
        const targetOffset = target.offsetTop - container.offsetTop;
        container.scrollTop = Math.max(0, targetOffset - container.clientHeight / 2);
      }
    };
    // Wait for the fade-in animation to complete before scrolling
    const timer = setTimeout(scrollToTarget, animationDelayMs + 350);
    return () => clearTimeout(timer);
  }, [messages.length, animationDelayMs]);

  const bucketColorClass = getBucketColor(conv.bucket);
  const instanceCount = conv.instanceCount || 1;

  return (
    <div
      className="border border-black/[0.08] rounded-lg overflow-hidden liquid-glass anim-lift"
      data-testid="proof-grid-card"
      data-has-contact={hasContact ? "true" : "false"}
    >
      {/* Card Header */}
      <div
        className="flex items-center gap-2 px-3 py-2 bg-black/[0.03] border-b border-black/[0.08] cursor-pointer hover:bg-black/[0.05] transition-colors"
        onClick={selectMode ? onToggleSelect : onHeaderClick}
        data-testid="proof-grid-card-header"
      >
        {selectMode && (
          <span
            className="shrink-0 flex items-center"
            title={hasContact ? undefined : "No GHL contact linked"}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={selected}
              disabled={!hasContact}
              onChange={onToggleSelect}
              data-testid="proof-grid-card-checkbox"
              style={{ accentColor: "var(--accent)" }}
              className="w-3.5 h-3.5 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            />
          </span>
        )}
        <div className={`w-2.5 h-2.5 rounded-sm shrink-0 ${bucketColorClass}`} />
        <div className="flex-1 min-w-0">
          <span className="token-11 font-medium text-glass truncate block">
            {conv.contact_name || "Unknown"}
          </span>
          {conv.lead_instagram && (
            <span className="token-10 text-glass-muted truncate block">@{conv.lead_instagram}</span>
          )}
        </div>
        <span className="token-10 text-glass-secondary shrink-0 bg-black/[0.05] px-1.5 py-0.5 rounded">
          {conv.time_label}
        </span>
        {conv.lifetime_label && (
          <span className="token-10 text-glass-muted shrink-0 px-1.5 py-0.5 rounded border border-black/[0.05]">
            {conv.lifetime_label}
          </span>
        )}
        {instanceCount > 1 && (
          <span className="token-10 text-ds-amber shrink-0 bg-ds-amber/15 px-1.5 py-0.5 rounded">
            {instanceCount}×
          </span>
        )}
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <CopyConversationButton
            variant="card"
            disabled={!messages.length}
            getText={() =>
              serializeConversation({
                messages,
                contactName: conv.contact_name || "Unknown",
                leadInstagram: conv.lead_instagram,
                timezone,
              })
            }
          />
        </div>
        <svg
          className="w-3 h-3 text-glass-muted shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>

      {/* Per-card actions (Layout B) — labeled row under the header. Hidden in select
          mode, where the bulk action bar owns Open/Dismiss/Kill instead. S64. */}
      {!selectMode && (
        <CardActions
          conversationId={conv.conversation_id}
          contactId={contactId}
          locationId={locationId}
          viewedSetterId={viewedSetterId}
          currentUserGhlId={currentUserGhlId}
          onAfterDismiss={onAfterDismiss}
        />
      )}

      {/* Card Body — Messages */}
      <div
        ref={scrollRef}
        className="max-h-80 overflow-y-auto p-2 bg-black/[0.03]"
        style={{ overscrollBehavior: "contain" }}
      >
        {messages.length === 0 ? (
          <p className="token-10 text-glass-muted text-center py-4">(no messages)</p>
        ) : (
          messages.map((msg, idx) => {
            const ht = getHighlightType(idx, highlightIndices, anchorIndices);
            // Assign scroll ref to the FIRST highlight only
            const isFirstHighlight = ht === "highlight" && !firstHighlightAssigned;
            if (isFirstHighlight) firstHighlightAssigned = true;

            // Dim messages outside active date range
            const dimmed = activeDateRange
              ? (() => {
                  const msgTime = new Date(msg.sent_at).getTime();
                  return (
                    msgTime < activeDateRange.start.getTime() ||
                    msgTime > activeDateRange.end.getTime()
                  );
                })()
              : false;

            // Track first in-range message for auto-scroll fallback
            const isFirstInRange = !dimmed && !firstInRangeAssigned && activeDateRange;
            if (isFirstInRange) firstInRangeAssigned = true;

            // For metrics without highlights, the last message gets the highlight ring + scroll target
            const isLastMessage = idx === messages.length - 1;
            const showAsHighlight = !hasExplicitHighlight && isLastMessage;

            // Scroll ref priority: explicit highlight > first in-range > last message
            const scrollTargetRef = isFirstHighlight
              ? highlightRef
              : isFirstInRange
                ? firstInRangeRef
                : showAsHighlight
                  ? lastMessageRef
                  : undefined;

            return (
              <MiniBubble
                key={msg.id}
                ref={scrollTargetRef}
                message={msg}
                timezone={timezone}
                highlightType={ht || (showAsHighlight ? "highlight" : undefined)}
                dimmed={dimmed}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

// ─── Main Grid Component ─────────────────────────────────────────────────────

export const ProofGridView: React.FC<ProofGridViewProps> = ({
  conversations,
  organizationId,
  timezone,
  onConversationClick,
  activeDateRange,
  viewedSetterId,
  currentUserGhlId,
  onAfterBulkDismiss,
}) => {
  const [messagesMap, setMessagesMap] = useState<Map<string, Message[]>>(new Map());
  const [contactIdMap, setContactIdMap] = useState<Map<string, string | null>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Paginate: show 9 conversations at a time, "Load More" to reveal next 9
  const PAGE_SIZE = 9;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Bulk-select state (S28)
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { locationId } = useGhlLocationId(organizationId);

  // Pre-fetch contact ids for the visible page so the BulkOpenInGHL click handler
  // is purely synchronous (no await between user gesture and window.open loop).
  const fetchContactIdsForIds = useCallback(
    async (idsToFetch: string[]): Promise<Map<string, string | null>> => {
      const result = new Map<string, string | null>();
      if (!supabase || idsToFetch.length === 0) return result;
      const CHUNK_SIZE = 100;
      for (let i = 0; i < idsToFetch.length; i += CHUNK_SIZE) {
        const chunk = idsToFetch.slice(i, i + CHUNK_SIZE);
        const { data, error: err } = await supabase
          .from("ghl_conversations")
          .select("id, ghl_contact_id")
          .in("id", chunk);
        if (err) throw err;
        for (const row of data || []) {
          const r = row as { id: string; ghl_contact_id: string | null };
          result.set(r.id, r.ghl_contact_id ?? null);
        }
      }
      return result;
    },
    []
  );

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ── Message fetcher (pure function, no side effects) ──────────────────────
  const fetchMessagesForIds = useCallback(
    async (idsToFetch: string[]): Promise<Map<string, Message[]>> => {
      const result = new Map<string, Message[]>();
      if (!supabase || idsToFetch.length === 0) return result;

      const CHUNK_SIZE = 100;
      const chunks: string[][] = [];
      for (let i = 0; i < idsToFetch.length; i += CHUNK_SIZE) {
        chunks.push(idsToFetch.slice(i, i + CHUNK_SIZE));
      }

      const results = await Promise.all(
        chunks.map((chunk) =>
          supabase!
            .from("ghl_messages")
            .select(
              "id, message_text, sent_at, sender_type, conversation_id, has_media, ghl_sender_user_id, sender_name, message_type"
            )
            .in("conversation_id", chunk)
            .not(
              "message_type",
              "in",
              "(TYPE_ACTIVITY_OPPORTUNITY,TYPE_INTERNAL_COMMENT,TYPE_CALL)"
            )
            .order("sent_at", { ascending: true })
            .limit(5000)
        )
      );

      const allData: (typeof results)[0]["data"] = [];
      for (const r of results) {
        if (r.error) throw r.error;
        if (r.data) allData.push(...r.data);
      }

      for (const row of allData) {
        const m = row as { message_text: string | null; conversation_id: string };
        if (
          m.message_text?.includes("Opportunity created") ||
          m.message_text?.includes("Opportunity updated") ||
          m.message_text?.includes("Opportunity deleted")
        )
          continue;
        const cid = m.conversation_id;
        if (!result.has(cid)) result.set(cid, []);
        result.get(cid)!.push(row as unknown as Message);
      }

      return result;
    },
    []
  );

  // ── Initial load: fetch messages for first 9 conversations ────────────────
  // No reactive useEffect on visibleCount — "Load more" handles its own fetching.
  const initialIdsRef = useRef<string[]>([]);
  useEffect(() => {
    const firstPageIds = [
      ...new Set(conversations.slice(0, PAGE_SIZE).map((c) => c.conversation_id)),
    ];
    // Skip if same conversations (prevent re-fetch on parent re-render)
    if (firstPageIds.join(",") === initialIdsRef.current.join(",")) return;
    initialIdsRef.current = firstPageIds;

    if (firstPageIds.length === 0) {
      queueMicrotask(() => setIsLoading(false));
      return;
    }

    // Check cache. Messages are cached; contact ids are NOT — they must be
    // refetched on every bucket open or bulk-select gating goes stale (the
    // checkbox `disabled={!hasContact}` reads contactIdMap, which would be empty
    // on cache-hit revisit, lying to the user with "No GHL contact linked").
    const cacheKey = firstPageIds.join(",");
    const cached = getGridMessagesCache<Record<string, Message[]>>(cacheKey, organizationId);

    const contactIdsPromise = fetchContactIdsForIds(firstPageIds).catch((err) => {
      console.warn("Failed to fetch grid contact ids:", err);
      return new Map<string, string | null>();
    });

    if (cached) {
      queueMicrotask(() => {
        setMessagesMap(new Map(Object.entries(cached)));
        setIsLoading(false);
      });
      contactIdsPromise.then((contacts) => setContactIdMap(contacts));
      return;
    }

    queueMicrotask(() => {
      setIsLoading(true);
      setError(null);
    });

    Promise.all([fetchMessagesForIds(firstPageIds), contactIdsPromise])
      .then(([entries, contacts]) => {
        setMessagesMap(entries);
        setContactIdMap(contacts);
        // Cache (messages only; contact ids are session-local)
        const obj: Record<string, Message[]> = {};
        entries.forEach((msgs, cid) => {
          obj[cid] = msgs;
        });
        setGridMessagesCache(cacheKey, organizationId, obj);
      })
      .catch((err) => {
        console.warn("Failed to fetch grid messages:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch messages");
      })
      .finally(() => setIsLoading(false));
  }, [conversations, organizationId, fetchMessagesForIds, fetchContactIdsForIds]);

  // ── "Load more" handler: fetch THEN reveal ────────────────────────────────
  const handleLoadMore = useCallback(async () => {
    const nextIds = [
      ...new Set(
        conversations.slice(visibleCount, visibleCount + PAGE_SIZE).map((c) => c.conversation_id)
      ),
    ];

    if (nextIds.length > 0) {
      setIsLoadingMore(true);
      try {
        const [newEntries, newContacts] = await Promise.all([
          fetchMessagesForIds(nextIds),
          fetchContactIdsForIds(nextIds).catch((err) => {
            console.warn("Failed to fetch grid contact ids (load more):", err);
            return new Map<string, string | null>();
          }),
        ]);
        // Merge into existing maps, THEN bump visible count in the same batch
        setMessagesMap((prev) => {
          const merged = new Map(prev);
          newEntries.forEach((msgs, cid) => merged.set(cid, msgs));
          return merged;
        });
        setContactIdMap((prev) => {
          const merged = new Map(prev);
          newContacts.forEach((contactId, cid) => merged.set(cid, contactId));
          return merged;
        });
      } catch {
        // Cards will show "(no messages)" as fallback — acceptable
      }
      setIsLoadingMore(false);
    }

    // Reveal the cards AFTER messages are in state
    setVisibleCount((v) => v + PAGE_SIZE);
  }, [conversations, visibleCount, fetchMessagesForIds, fetchContactIdsForIds]);

  // Reset visible count when conversations list changes (e.g. different metric/setter)
  // MUST be before early returns — React hooks must always execute in the same order
  const prevConvKey = useRef(conversations.map((c) => c.conversation_id).join(","));
  useEffect(() => {
    const key = conversations.map((c) => c.conversation_id).join(",");
    if (key !== prevConvKey.current) {
      prevConvKey.current = key;
      queueMicrotask(() => setVisibleCount(PAGE_SIZE));
    }
  }, [conversations]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="proof-grid-view">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-glass-muted">Loading all conversations...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-ds-red/10 border border-ds-red/30 rounded-lg p-4 text-ds-red token-14"
        data-testid="proof-grid-view"
      >
        Error loading messages: {error}
      </div>
    );
  }

  const visibleConversations = conversations.slice(0, visibleCount);
  const hasMore = visibleCount < conversations.length;
  const hasContact = (id: string): boolean => {
    const cid = contactIdMap.get(id);
    return cid !== null && cid !== undefined;
  };
  const visibleSelectableCount = visibleConversations.filter((c) =>
    hasContact(c.conversation_id)
  ).length;

  const handleSelectToggle = () => {
    if (selectMode) {
      setSelectedIds(new Set());
      setSelectMode(false);
    } else {
      setSelectMode(true);
    }
  };

  return (
    <div data-testid="proof-grid-view">
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="token-11 text-glass-muted">
          {selectMode
            ? `${selectedIds.size} of ${visibleSelectableCount} selected`
            : `${conversations.length} conversations · Click any card header to open full view`}
        </p>
        <button
          type="button"
          onClick={handleSelectToggle}
          data-testid="proof-grid-select-toggle"
          className="liquid-glass-light px-3 py-1 token-11 text-glass-secondary hover:text-glass shrink-0"
        >
          {selectMode ? "Cancel" : "Select"}
        </button>
      </div>
      {selectMode && (
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="proof-grid-action-bar">
          <BulkOpenInGHL
            ids={Array.from(selectedIds)}
            contactIdMap={contactIdMap}
            locationId={locationId}
            getName={(id) =>
              conversations.find((c) => c.conversation_id === id)?.contact_name || "Unknown"
            }
          />
          <BulkDismiss
            ids={Array.from(selectedIds)}
            viewedSetterId={viewedSetterId}
            currentUserGhlId={currentUserGhlId}
            onAfterDismiss={(dismissedIds, dismissType) => {
              // Optimistically clear selection; parent owns refetch + cache invalidation.
              setSelectedIds(new Set());
              if (onAfterBulkDismiss) onAfterBulkDismiss(dismissedIds, dismissType);
            }}
          />
          <button
            type="button"
            onClick={() =>
              setSelectedIds(
                new Set(
                  visibleConversations
                    .filter((c) => hasContact(c.conversation_id))
                    .map((c) => c.conversation_id)
                )
              )
            }
            data-testid="proof-grid-select-all-visible"
            className="liquid-glass-light px-3 py-1 token-11 text-glass-secondary hover:text-glass"
          >
            Select all (visible)
          </button>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            data-testid="proof-grid-clear-selection"
            className="liquid-glass-light px-3 py-1 token-11 text-glass-secondary hover:text-glass"
          >
            Clear
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mx-8">
        {visibleConversations.map((conv, i) => (
          <div
            key={conv.conversation_id}
            className="animate-[fadeSlideIn_0.3s_ease-out_forwards]"
            style={{ animationDelay: `${(i % 9) * 30}ms`, opacity: 0 }}
          >
            <ConversationCard
              conv={conv}
              messages={messagesMap.get(conv.conversation_id) || []}
              timezone={timezone}
              animationDelayMs={(i % 9) * 30}
              onHeaderClick={() => {
                // P1 — siblings = the FULL filtered array in screen order (swipe
                // past the visible 9), index found by id. hasMore=false: the
                // bucket auto-loads pages as you read, so v1 swipes the loaded set.
                const siblings = conversations.map((c) => ({
                  conversationId: c.conversation_id,
                  contactName: c.contact_name || "Unknown",
                  leadInstagram: c.lead_instagram ?? null,
                }));
                const index = conversations.findIndex(
                  (c) => c.conversation_id === conv.conversation_id
                );
                onConversationClick(
                  conv.conversation_id,
                  conv.contact_name || "Unknown",
                  conv.lead_instagram,
                  undefined,
                  siblings.length > 1 ? { siblings, index, hasMore: false } : undefined
                );
              }}
              activeDateRange={activeDateRange}
              selectMode={selectMode}
              selected={selectedIds.has(conv.conversation_id)}
              hasContact={hasContact(conv.conversation_id)}
              onToggleSelect={() => {
                if (hasContact(conv.conversation_id)) toggleSelect(conv.conversation_id);
              }}
              contactId={contactIdMap.get(conv.conversation_id) ?? null}
              locationId={locationId}
              viewedSetterId={viewedSetterId}
              currentUserGhlId={currentUserGhlId}
              onAfterDismiss={(ids, type) => {
                if (onAfterBulkDismiss) onAfterBulkDismiss(ids, type);
              }}
            />
          </div>
        ))}
      </div>
      {hasMore && (
        <div className="flex justify-center mt-4">
          <button
            disabled={isLoadingMore}
            onClick={handleLoadMore}
            className="anim-tap px-4 py-2 token-14 rounded-lg bg-black/[0.05] hover:bg-black/[0.08] text-glass-secondary transition-colors disabled:opacity-50"
          >
            {isLoadingMore
              ? "Loading..."
              : `Load more (${conversations.length - visibleCount} remaining)`}
          </button>
        </div>
      )}
    </div>
  );
};
